import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import CommunicationService from '../../services/CommunicationService';
import {
  Send as SendIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  MarkAsUnread as UnreadIcon,
  Flag as PriorityIcon,
  Attachment as AttachmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VideoCall as VideoCallIcon,
  Chat as ChatIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

interface Message {
  id: number;
  subject: string;
  sender: string;
  senderEmail: string;
  content: string;
  timestamp: string;
  type: 'email' | 'chat' | 'notification' | 'phone';
  priority: 'high' | 'medium' | 'low';
  isRead: boolean;
  hasAttachment: boolean;
  project?: string;
  threadId?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`communication-tabpanel-${index}`}
      aria-labelledby={`communication-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Communication Center Component for Project Manager
 * Handles messages, notifications, and communication with team members
 */
const CommunicationCenter: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyContent, setReplyContent] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      setError(null);
      setLoading(true);

      // Fetch messages from API
      const fetchedMessages = await CommunicationService.getCommunications();
      setMessages(fetchedMessages);
    } catch (err) {
      setError('Failed to load messages. Please try again.');
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, message: Message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await CommunicationService.markAsRead(messageId);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, isRead: true } : msg
      ));
    } catch (err) {
      console.error('Error marking message as read:', err);
      setError('Failed to mark message as read.');
    }
    handleMenuClose();
  };

  const getMessagesByType = (type: string) => {
    return messages.filter(msg => {
      if (type === 'all') return true;
      if (type === 'unread') return !msg.isRead;
      return msg.type === type;
    });
  };

  const getFilteredMessages = (type: string) => {
    const typeFiltered = getMessagesByType(type);
    if (!searchTerm) return typeFiltered;
    
    return typeFiltered.filter(msg => 
      msg.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <EmailIcon />;
      case 'chat': return <ChatIcon />;
      case 'notification': return <NotificationsIcon />;
      case 'phone': return <PhoneIcon />;
      default: return <EmailIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const unreadCount = messages.filter(msg => !msg.isRead).length;
  const emailCount = messages.filter(msg => msg.type === 'email').length;
  const chatCount = messages.filter(msg => msg.type === 'chat').length;
  const notificationCount = messages.filter(msg => msg.type === 'notification').length;

  const tabs = [
    { label: 'All Messages', type: 'all', count: messages.length },
    { label: 'Unread', type: 'unread', count: unreadCount },
    { label: 'Emails', type: 'email', count: emailCount },
    { label: 'Chat', type: 'chat', count: chatCount },
    { label: 'Notifications', type: 'notification', count: notificationCount },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Communication Center
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<VideoCallIcon />}>
            Start Meeting
          </Button>
          <Button variant="contained" startIcon={<SendIcon />} onClick={() => setComposeOpen(true)}>
            Compose
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadMessages}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="communication tabs">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.type}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    {tab.label}
                    {tab.count > 0 && (
                      <Chip 
                        label={tab.count} 
                        size="small" 
                        color={tab.type === 'unread' ? 'error' : 'default'}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        <CardContent>
          {/* Search Bar */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {/* Message List */}
          {tabs.map((tab, index) => (
            <TabPanel key={tab.type} value={activeTab} index={index}>
              <List>
                {getFilteredMessages(tab.type).map((message, msgIndex) => (
                  <React.Fragment key={message.id}>
                    <ListItem
                      alignItems="flex-start"
                      sx={{ 
                        backgroundColor: message.isRead ? 'transparent' : 'action.hover',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          badgeContent={message.isRead ? 0 : 1}
                          color="error"
                          variant="dot"
                        >
                          <Avatar sx={{ bgcolor: getPriorityColor(message.priority) + '.main' }}>
                            {getTypeIcon(message.type)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={message.isRead ? 'normal' : 'bold'}
                              sx={{ flex: 1 }}
                            >
                              {message.subject}
                            </Typography>
                            {message.hasAttachment && <AttachmentIcon fontSize="small" />}
                            <Chip 
                              label={message.priority} 
                              size="small" 
                              color={getPriorityColor(message.priority) as any}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              <strong>From:</strong> {message.sender} ({message.senderEmail})
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {message.content.length > 100 
                                ? `${message.content.substring(0, 100)}...` 
                                : message.content
                              }
                            </Typography>
                            {message.project && (
                              <Chip 
                                label={message.project} 
                                size="small" 
                                variant="outlined"
                                sx={{ mt: 1 }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(message.timestamp)}
                          </Typography>
                          <IconButton
                            edge="end"
                            onClick={(e) => handleMenuOpen(e, message)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {msgIndex < getFilteredMessages(tab.type).length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                {getFilteredMessages(tab.type).length === 0 && (
                  <Box textAlign="center" py={4}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Messages
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No messages match your search.' : `No ${tab.label.toLowerCase()} found.`}
                    </Typography>
                  </Box>
                )}
              </List>
            </TabPanel>
          ))}
        </CardContent>
      </Card>

      {/* Message Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedMessage && !selectedMessage.isRead && (
          <MenuItem onClick={() => handleMarkAsRead(selectedMessage.id)}>
            <UnreadIcon sx={{ mr: 1 }} fontSize="small" />
            Mark as Read
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuClose}>
          <ReplyIcon sx={{ mr: 1 }} fontSize="small" />
          Reply
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ForwardIcon sx={{ mr: 1 }} fontSize="small" />
          Forward
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
          Archive
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compose Message</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="To"
              fullWidth
              placeholder="Enter email addresses..."
            />
            <TextField
              label="Subject"
              fullWidth
              placeholder="Enter subject..."
            />
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select defaultValue="medium" label="Priority">
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Message"
              multiline
              rows={6}
              fullWidth
              placeholder="Enter your message..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon />}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationCenter;